import { SPACING } from "./readOnlyStyles";
import { getScoreColor } from "./scoring";

const passage = {
  flex: 1,
  minWidth: 0,
  overflowWrap: "anywhere",
  fontSize: 15,
  lineHeight: "22px",
  color: "#2C2B80",
  backgroundColor: "#FBFAFF",
  borderRadius: 12,
  padding: SPACING.sm,
  margin: 0,
} as const;

export interface ScoredWord {
  word: string;
  accuracyScore: number;
}

/** What survives tokenization — each segment is stripped with it before matching. */
const WORD_CHARS_PATTERN = /[^\w'-]/g;

const normalizeForMatch = (text: string) => text.replace(/[^\w-]/g, "").toLowerCase();

/** How far past the expected token a scored word may search before it is dropped. */
const LOOKAHEAD_TOKENS = 5;

/**
 * Matches stored word scores to the passage's tokens with a cursor rather than by position: a
 * student who skipped or repeated a word leaves the two arrays different lengths, and indexing
 * straight across would colour every later word with some other word's score. A word that finds
 * no match inside the lookahead window is dropped — uncoloured beats coloured wrongly.
 *
 * Simpler than the live reader's alignment, which also reconciles Azure's duplicate and phantom
 * emissions so its colours agree with the state machine mid-sentence. Nothing here is live.
 */
function alignScoresToTokens(
  tokens: readonly string[],
  words: readonly ScoredWord[]
): (ScoredWord | undefined)[] {
  const normalizedTokens = tokens.map(normalizeForMatch);
  const result: (ScoredWord | undefined)[] = new Array(tokens.length).fill(undefined);
  let cursor = 0;

  for (const scored of words) {
    const expected = normalizedTokens[cursor];
    if (expected === undefined) break;

    const actual = normalizeForMatch(scored.word);
    if (expected === actual) {
      result[cursor] = scored;
      cursor++;
      continue;
    }

    const lookaheadEnd = Math.min(cursor + LOOKAHEAD_TOKENS, tokens.length);
    for (let index = cursor + 1; index < lookaheadEnd; index++) {
      if (normalizedTokens[index] === actual) {
        result[index] = scored;
        cursor = index + 1;
        break;
      }
    }
  }

  return result;
}

/**
 * The passage coloured word by word from the stored accuracy scores, on the same thresholds the
 * student saw live. A word with no stored score keeps the plain passage colour — an unscored word
 * is a better answer than a guessed one.
 */
export function ScoredPassageText({ text, words }: { text: string; words: readonly ScoredWord[] }) {
  const segments = text.split(/(\s+)/);
  const tokens = segments
    .map((segment) => segment.replace(WORD_CHARS_PATTERN, ""))
    .filter((word) => word.length > 0);
  const scoredByToken = alignScoresToTokens(tokens, words);

  let charOffset = 0;
  let tokenIndex = 0;

  return (
    <p style={passage}>
      {segments.map((segment) => {
        const key = `${charOffset}`;
        charOffset += segment.length;

        const cleanWord = segment.replace(WORD_CHARS_PATTERN, "");
        if (!cleanWord) return <span key={key}>{segment}</span>;

        const scored = scoredByToken[tokenIndex++];
        return (
          <span
            key={key}
            style={scored ? { color: getScoreColor(scored.accuracyScore) } : undefined}
          >
            {segment}
          </span>
        );
      })}
    </p>
  );
}
